import * as React from 'react';

import classNames from 'classnames';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';
import type {FilterObject} from 'nutshell-core/types';

import {AiFeatureType} from 'nutshell-graphql-types';

import {analyticsClient} from '../../analytics-client';

import {useGetEntity} from '../core/use-get-entity';
import {useGetSessionUser} from '../core/use-get-session-user';
import {useUpdateUserSetting} from '../core/use-update-user-setting';
import {TimelineSummary} from '../power-ai/timeline-summary/timeline-summary';
import {useIsAiFeatureEnabled} from '../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import {Changelog} from './changelog/changelog';
import {TimelineCreator} from './creator/timeline-creator';
import {getEventsQueryVariables} from './graphql/graphql-wrappers/helpers';
import {useGetPinnedEvents} from './graphql/graphql-wrappers/pinned-timeline-events-query';
import {useGetTimelineEvents} from './graphql/graphql-wrappers/use-get-timeline-events';
import {useGetOpenThread} from './graphql/hooks';
import {useDeleteEvent, useSetPinnedStatus} from './graphql/timeline-event-mutations';
import {updateFilters} from './helpers';
import {Timeline as TimelineComponent} from './timeline';
import {TimelineFilterBar} from './timeline-filter-bar';
import {TimelinePinnedEvents} from './timeline-pinned-events';
import type {EntityName} from './types';

import styles from './entity-page-timeline.module.css';

type Props = {
    entityId: string;
    entityName: EntityName;
    entityPageName: string | null | undefined;
    scrollContainerRef: HTMLDivElement | null | undefined;
    isFullWidth?: boolean;
    hideCreator?: boolean;
    useContainerScroll?: boolean;
};

export function EntityPageTimeline(props: Props) {
    const {entityId, entityName, entityPageName, scrollContainerRef, isFullWidth = true} = props;
    const [activeFilters, setActiveFilters] = React.useState<FilterObject[]>([]);
    const [isViewingChangelog, setIsViewingChangelog] = React.useState<boolean>(false);

    const deleteEvent = useDeleteEvent();
    const onDelete = (eventId: string) => deleteEvent({changeLogId: eventId, queryVariables});

    const setPinnedStatus = useSetPinnedStatus();
    const onSetPinnedStatus = (setStatusInput: {
        entityToPinId: string;
        pinToEntityId: string;
        status: boolean;
        userName: string;
        changeLogId: string;
    }) => setPinnedStatus({setStatusInput, queryVariables});

    const {user} = useGetSessionUser();
    const {entity} = useGetEntity(entityId);
    const {pinnedEvents, refetch: refetchPinnedEvents} = useGetPinnedEvents({
        entityId,
    });
    const {openThread, refetch: refetchOpenThread} = useGetOpenThread(entityId);

    const updateUserSetting = useUpdateUserSetting();
    const setExcludeOutreach = () =>
        updateUserSetting({
            userId: user ? user.id : '',
            settingName: Nut_Settings_User.SETTING_EXCLUDE_TIMELINE_OUTREACH_EVENTS,
            value: excludeOutreach ? '0' : '1',
            propertyOnUser: 'isExcludingTimelineOutreach',
        });
    const setExcludeInteractions = () =>
        updateUserSetting({
            userId: user ? user.id : '',
            settingName: Nut_Settings_User.SETTING_EXCLUDE_TIMELINE_INTERACTION_EVENTS,
            value: excludeInteractions ? '0' : '1',
            propertyOnUser: 'isExcludingTimelineInteractions',
        });
    const setExcludeCustomerJourneys = () =>
        updateUserSetting({
            userId: user ? user.id : '',
            settingName: Nut_Settings_User.SETTING_EXCLUDE_TIMELINE_CUSTOMER_JOURNEYS,
            value: excludeCustomerJourneys ? '0' : '1',
            propertyOnUser: 'isExcludingTimelineCustomerJourneys',
        });

    const excludeOutreach = Boolean(user && user.isExcludingTimelineOutreach);
    const excludeInteractions = Boolean(user && user.isExcludingTimelineInteractions);
    const excludeCustomerJourneys = Boolean(user && user.isExcludingTimelineCustomerJourneys);

    const queryVariables = getEventsQueryVariables({
        entityId,
        activeFilters,
        excludeOutreach,
        excludeInteractions,
        excludeCustomerJourneys,
        after: null,
    });

    const {
        events,
        fetchMostRecentEvents,
        isLoading,
        isFirstLoad,
        error,
        fetchMoreEvents,
        hasNextPage,
        refetchEvents,
    } = useGetTimelineEvents(queryVariables);

    const refetchTimelineEvents = () => {
        refetchEvents();
        refetchPinnedEvents();
        refetchOpenThread();
    };

    const {isEnabled: isTimelineSummarizerOutcomeEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.TIMELINE_SUMMARIZER
    );

    React.useEffect(() => {
        $(window).on(Timeline.events.TimelineRefresh, refetchTimelineEvents);

        return () => {
            $(window).off(Timeline.events.TimelineRefresh, refetchTimelineEvents);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!user || !entity) return <></>;

    const onUpdateFilters = (filterObject: FilterObject, filterName: string) => {
        if (filterName === 'activityType') {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_TIMELINE_FILTER_ACTIVITY_TYPE);
        }

        const updatedFilters = updateFilters(
            filterObject as FilterObject,
            filterName as string,
            activeFilters
        );

        setActiveFilters(updatedFilters);
    };
    const onRemoveAllFilters = () => {
        setActiveFilters([]);
    };

    const containerClasses = classNames({
        [styles['timeline-container']]: isFullWidth,
    });

    return (
        <div className={containerClasses}>
            {!props.hideCreator && (
                <TimelineCreator
                    entityId={entityId}
                    entityName={entityName}
                    entityPageName={entityPageName}
                    refetchEvents={fetchMostRecentEvents}
                />
            )}
            <TimelineFilterBar
                allowFilteringOnLeadUpdates={entityName === 'Leads'}
                hasCalculatedWidth={!props.hideCreator && !props.isFullWidth}
                activeFilters={activeFilters}
                onUpdateFilters={onUpdateFilters}
                onRemoveAllFilters={onRemoveAllFilters}
                entityCreatedTime={entity.createdTime}
                excludeOutreach={excludeOutreach}
                excludeInteractions={excludeInteractions}
                excludeCustomerJourneys={excludeCustomerJourneys}
                setExcludeOutreach={setExcludeOutreach}
                setExcludeInteractions={setExcludeInteractions}
                setExcludeCustomerJourneys={setExcludeCustomerJourneys}
                isViewingChangelog={isViewingChangelog}
                setIsViewingChangelog={setIsViewingChangelog}
                // Don't show the summarize button when there are less than 5 events shown or AI is not enabled
                shouldShowSummarizeButton={events.length >= 5 && isTimelineSummarizerOutcomeEnabled}
                entityId={entityId}
            />
            {isViewingChangelog ? (
                <Changelog
                    entityId={entityId}
                    entityName={entityName}
                    entityPageName={entityPageName || 'Unknown'}
                />
            ) : (
                <>
                    {isTimelineSummarizerOutcomeEnabled ? (
                        <TimelineSummary entityId={entityId} />
                    ) : undefined}
                    <TimelinePinnedEvents
                        openThread={openThread}
                        pinnedEvents={pinnedEvents}
                        entityId={entityId}
                        user={user}
                        entityPageName={entityPageName}
                        onSetPinnedStatus={onSetPinnedStatus}
                    />
                    <TimelineComponent
                        events={events}
                        user={user}
                        entityPageId={entityId}
                        entityPageName={entityPageName}
                        isLoading={isLoading}
                        isFirstLoad={isFirstLoad}
                        hasNextPage={hasNextPage}
                        error={error}
                        refetchEvents={fetchMostRecentEvents}
                        onFetchMoreEvents={fetchMoreEvents}
                        scrollContainerRef={scrollContainerRef}
                        onDelete={onDelete}
                        onSetPinnedStatus={onSetPinnedStatus}
                        numPinnedEvents={pinnedEvents.length}
                        onRemoveAllFilters={onRemoveAllFilters}
                        useContainerScroll={props.useContainerScroll}
                    />
                </>
            )}
        </div>
    );
}
